import { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AuthContext } from '../../../App';
import { decode_utf8, useNotifications } from '../../../hooks';
import { sellsService, usersService } from '../../../services';
import { GridColDef } from '@material-ui/data-grid';
import { Grid, Button, createStyles, makeStyles, Theme } from '@material-ui/core';
import { DefaultGrid } from '../../../components';
import {
  zones,
  buyerTypes,
  sellSatus,
  deliveryStatus,
  DELIVERY_STATUSES,
} from '../../../constants';
import { SelectionAction } from '../../../components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      marginRight: theme.spacing(1),
    },
  })
);

function fullName(obj: any): string {
  if (!obj) return 'Desconosido';

  if (obj.firstname && obj.lastname) {
    obj.firstName = obj.firstname;
    obj.lastName = obj.lastname;
  }

  return [obj.firstName, obj.lastName].filter(Boolean).join(' ');
}

function formatDeliveryMans(data: any) {
  return data.map((x: any) => ({
    value: x.id,
    description: `${x.firstName} ${x.lastName}`,
  }));
}

const getPriceByTypeclient = (product: any, typeclient: any) => {
  const tc = Number(typeclient);
  switch (tc) {
    case 1:
      return product?.retailPrice;
    case 4:
      return product?.fullPrice2;
    case 5:
      return product?.fullPrice3;
    case 6:
      return product?.gridPrice;
    case 2:
    case 3:
    default:
      return product?.fullPrice;
  }
};

const getSellerCommissionByTypeclient = (product: any, typeclient: any) => {
  const tc = Number(typeclient);
  switch (tc) {
    case 1:
      return product?.sellerCommissionRetail;
    case 4:
      return product?.sellerCommissionFull2;
    case 5:
      return product?.sellerCommissionFull3;
    case 6:
      return product?.sellerCommissionGrid;
    case 2:
    case 3:
    default:
      return product?.sellerCommissionFull;
  }
};

const getCollaboratorCommissionByTypeclient = (product: any, typeclient: any) => {
  const tc = Number(typeclient);
  switch (tc) {
    case 1:
      return product?.collaboratorCommissionRetail;
    case 4:
      return product?.collaboratorCommissionFull2;
    case 5:
      return product?.collaboratorCommissionFull3;
    case 6:
      return product?.collaboratorCommissionGrid;
    case 2:
    case 3:
    default:
      return product?.collaboratorCommissionFull;
  }
};

const getColumns = (sell: any): GridColDef[] => {
  const productCar = sell?.productCar || [];
  const isDeposito = sell?.zone === 5;

  const cols: GridColDef[] = [
    { field: 'sku', headerName: 'SKU', width: 150 },
    { field: 'name', headerName: 'Nombre', width: 350 },
    { field: 'category', headerName: 'Categoría', valueGetter: ({ row }) => row.category?.name || '' },
    { field: 'subCategory', headerName: 'Sub-categoría', valueGetter: ({ row }) => row.subCategory?.name || '' },
    {
      field: 'fullPrice',
      headerName: 'Precio',
      valueGetter: ({ row }) => {
        const typeclient = sell?.client?.typeclient;
        const productCarPrice = productCar.find((pc: any) => pc.idProduct === row.id)?.price;
        const typeclientPrice = getPriceByTypeclient(row, typeclient);
        return Number(typeclient) === 4 ? typeclientPrice : productCarPrice ?? typeclientPrice;
      },
    },
    { field: 'stock', headerName: 'Cantidad', valueGetter: ({ row }) => productCar.find((pc: any) => pc.idProduct === row.id)?.quantity || 0 },
  ];

  if (!isDeposito) {
    cols.splice(5, 0, {
      field: 'shippingUnitCost',
      headerName: 'Costo Unit. Envío',
      valueGetter: ({ row }) => {
        const base = Number(row.shippingUnitCost) || 0;
        return (sell?.client?.typeclient === 2 || sell?.client?.typeclient === 4) ? base / 2 : base;
      },
    });
  }

  return cols;
};


const SellsDetails = () => {
  const { id } = useParams<{ id?: string }>();
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState<boolean>(false);
  const { userState } = useContext(AuthContext);
  const [sell, setSell] = useState<any>({});
  const [deliveryMans, setDeliveryMans] = useState<any>([]);
  const showCollaborator = ['admin', 'deposit'].includes(userState.type);
  const classes = useStyles();
  const history = useHistory();

  const getSell = async () => {
    setLoading(true);
    try {
      const response = await sellsService.find(id);
  
      if (response && response.productCar && response.product) {
        const totalCostCalculado = calcularTotalCost(response);
        setSell({ ...response, totalcost: totalCostCalculado });
      } else {
        setSell(response);
      }
  
      const responseDeliveryMans = await usersService.getDeliverymans();
      setDeliveryMans(responseDeliveryMans);
    } catch (e) {
      showNotification(
        'error',
        sellsService.getMessages('GET_COLLABORATORS', e).error
      );
    } finally {
      setLoading(false);
    }
  };  

  useEffect(() => {
    getSell();
  }, []);
  
  const handleBack = () => {
    history.goBack();
  };

  if (loading) return <div>loading...</div>;

  const {
    client = {},
    seller = {},
    product,
    collaborator = {},
    deliveryMan = {},
  } = sell;

  const isDelivered = DELIVERY_STATUSES.delivered === sell.deliverystate;

  const handleAssignmentConfirmation = async (deliveryManId: any) => {
    try {
      await sellsService.assignDeliveryMan(sell.nroVenta, deliveryManId);
      showNotification('success', 'Repartidor asignado correctamente');
      setSell({
        ...sell,
        deliveryMan: deliveryMans.find((x: any) => x.id === deliveryManId),
      });
    } catch {
      showNotification(
        'error',
        'Se produjo un error al intentar asignar al repartidor'
      );
    }
  };

  const calcularTotalCost = (sell: any) => {
    if (!sell.product || !sell.productCar) return 0;

    return sell.productCar.reduce((total: number, productoCar: any) => {
      const productInfo = sell.product.find((p: any) => p.id === productoCar.idProduct);

      const quantity = Number(productoCar.quantity) || 0;

      const typeclient = sell.client?.typeclient;
      const fallbackPrice = getPriceByTypeclient(productInfo, typeclient) || 0;
      const price = Number(typeclient) === 4
        ? Number(fallbackPrice) || 0
        : Number(productoCar.price) || Number(fallbackPrice) || 0;


      // ✅ Depósito: NO cuenta shipping
      if (sell.zone === 5) return total + quantity * price;

      const baseShippingUnitCost = Number(productInfo?.shippingUnitCost) || 0;
      const shippingUnitCost =
        (sell.client?.typeclient === 2 || sell.client?.typeclient === 4) ? baseShippingUnitCost / 2 : baseShippingUnitCost;

      return total + quantity * (price + shippingUnitCost);
    }, 0);
  };

  const calcularComisiones = (sell: any) => {
    if (!sell.product || !sell.productCar) return { seller: 0, collaborator: 0 };

    return sell.productCar.reduce(
      (total: { seller: number; collaborator: number }, productoCar: any) => {
        const productInfo = sell.product.find((p: any) => p.id === productoCar.idProduct);
        const quantity = Number(productoCar.quantity ?? productoCar.Quantity) || 0;

        const typeclient = sell.client?.typeclient;

        const sellerComission = Number(typeclient) === 4
          ? Number(getSellerCommissionByTypeclient(productInfo, typeclient)) || 0
          : Number(productoCar.sellerComission) ||
            Number(getSellerCommissionByTypeclient(productInfo, typeclient)) ||
            0;

        const collaboratorComission = Number(typeclient) === 4
          ? Number(getCollaboratorCommissionByTypeclient(productInfo, typeclient)) || 0
          : Number(productoCar.collaboratorComission) ||
            Number(getCollaboratorCommissionByTypeclient(productInfo, typeclient)) ||
            0;

        return {
          seller: total.seller + quantity * sellerComission,
          collaborator: total.collaborator + quantity * collaboratorComission,
        };
      },
      { seller: 0, collaborator: 0 }
    );
  };

  const { seller: sellerCommissionTotal, collaborator: collaboratorCommissionTotal } =
    calcularComisiones(sell);

  const sellerCommissionDisplay =
    sell.client?.typeclient === 4 ? sellerCommissionTotal : sell.sellercomission ?? sellerCommissionTotal;

  const collaboratorCommissionDisplay =
    sell.client?.typeclient === 4
      ? collaboratorCommissionTotal
      : sell.colaboradorcomission ?? collaboratorCommissionTotal;

  return (
    <div>
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Button onClick={handleBack} className={classes.button} type="button">
            Volver
        </Button>
        <h1>No Venta {sell.nroVenta}</h1>
        <div>
          {sell && userState.type === 'admin' && !isDelivered && sell.zone===5 && (
            <SelectionAction
              tooltip="Asignar repartidor"
              handleSelection={handleAssignmentConfirmation}
              message="Seleccione un repartidor"
              options={formatDeliveryMans(deliveryMans)}
              children={
                <Button variant="outlined" color="primary" type="button">
                  Asignar Repartidor
                </Button>
              }
            />
          )}
        </div>
      </Grid>
      {showCollaborator && <h3>Collaborator: {fullName(collaborator)}</h3>}
      {product && (
        <DefaultGrid
          rows={product}
          columns={getColumns(sell)}
          pageSize={5}
          height={300}
        />
      )}

      <br />
      {sell.zone === 5 ? (
        <ul>
          <li>Precio total de la venta: {sell.totalcost}</li>
        </ul>
      ) : (
        <ul>
          <li>Subtotal Productos: {sell.totalcost}</li>
          <li>Adicional por Localidad: {sell.deliverycost}</li>
          <li>Venta Flete Full: {client.ventaFlete}</li>
          <li>Precio total de la venta: {sell.totalcost + sell.deliverycost + client.ventaFlete}</li>
        </ul>
      )}
      <ul>
        <li>Comisión vendedor: {sellerCommissionDisplay}</li>
        <li>Comisión colaborador: {collaboratorCommissionDisplay}</li>
        <li>
          Fecha de venta: {new Date(sell.saledate).toLocaleDateString('es-ES')}
        </li>
        <li>Vendedor: {decode_utf8(fullName(seller))}</li>
      </ul>

      <h3>Datos del comprador</h3>
      <ul>
        <li>Nombre: {decode_utf8(fullName(sell.client))}</li>
        <li>DNI : {client.dni}</li>
        <li>
          Dirección de envío: ubicación de Google Map, piso, depto. :{' '}
          {decode_utf8(client.address)}
        </li>
        <li>Entre calles : {decode_utf8(client.beetwenstreets)}</li>
        <li>Zona : {zones[client.zone]}</li>
        <li>Comentarios : {decode_utf8(client.comments)}</li>
        <li>Email : {client.email}</li>
        <li>Teléfono : {client.phone}</li>
        <li>Tipo comprador : {buyerTypes[client.typeclient]}</li>
      </ul>

      <h3>Datos de la venta</h3>
      <ul>
        <li>Estado de venta: {sellSatus[sell.salestate]}</li>
        <li>Estado de entrega: {deliveryStatus[sell.deliverystate]} </li>
        <li>Repartidor: {decode_utf8(fullName(deliveryMan))}</li>
        <li>Comentarios : {decode_utf8(sell.comments)}</li>
      </ul>
    </div>
  );
};

export default SellsDetails;
